import { useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { AuthContext, type AuthContextValue } from '@/hooks/useAuth';
import { fetchCurrentUser, login as apiLogin, logout as apiLogout } from '@/api/auth';
import { clearAuthToken, getAuthToken, setAuthToken } from '@/lib/api';
import type { User } from '@/types';

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const queryClient = useQueryClient();
  // LINE Login 導回時帶 ?line_token=<jwt>，先取出存好再初始化 state。
  const initialToken = (() => {
    try {
      const p = new URLSearchParams(window.location.search);
      const lt = p.get('line_token');
      if (lt) {
        setAuthToken(lt);
        p.delete('line_token');
        const clean = window.location.pathname + (p.toString() ? `?${p}` : '');
        window.history.replaceState({}, '', clean);
        return lt;
      }
    } catch {
      /* ignore */
    }
    return getAuthToken();
  })();
  const [token, setToken] = useState<string | null>(initialToken);
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(!!initialToken);

  // On mount, if we have a token, hydrate the user.
  useEffect(() => {
    let cancelled = false;
    if (!token) {
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    fetchCurrentUser()
      .then((u) => {
        if (!cancelled) setUser(u);
      })
      .catch(() => {
        // Token invalid — clear it.
        clearAuthToken();
        if (!cancelled) {
          setToken(null);
          setUser(null);
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  const value: AuthContextValue = useMemo(
    () => ({
      user,
      token,
      isAuthenticated: !!token && !!user,
      isLoading,
      login: async (email: string, password: string) => {
        const result = await apiLogin({ email, password });
        setAuthToken(result.access_token);
        setToken(result.access_token);
        setUser(result.user);
      },
      logout: () => {
        apiLogout();
        clearAuthToken();
        setToken(null);
        setUser(null);
        queryClient.clear();
      },
    }),
    [user, token, isLoading, queryClient],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
